import { Decamelized } from 'humps'
import { Attachment } from 'shared/models/document'
import { ContactedLoad, LoadStopProgressStatus, TrackingStatus } from 'shared/models/load'
import { SlimTeam } from 'shared/models/team'
import { error, errorD1, lightgrey, marigoldOrange, success, successD1, warning } from 'shared/theme/colors'

import { EquipmentType } from './equipment'
import { TruckV1 } from './truck'
import { User } from './user'

interface TruckercloudIntegrationStatusProps {
  [status: string]: string
}

export enum ConnectedIntegrationStatusEnum {
  CONNECTED = 'CONNECTED',
  ERROR = 'ERROR',
  PENDING = 'PENDING',
  NOT_STARTED = 'NOT_STARTED',
}

export enum TruckercloudIntegrationStatusEnum {
  NO_DATA = 'NO_DATA',
  DATA_AVAILABLE = 'DATA_AVAILABLE',
  NEEDS_VERIFICATION = 'NEEDS_VERIFICATION',
  VERIFIED = 'VERIFIED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export const TruckercloudIntegrationStatusText: TruckercloudIntegrationStatusProps = {
  [TruckercloudIntegrationStatusEnum.NO_DATA]: 'Data unavailable',
  [TruckercloudIntegrationStatusEnum.DATA_AVAILABLE]: 'Verification in progress',
  [TruckercloudIntegrationStatusEnum.NEEDS_VERIFICATION]: 'Verification needed',
  [TruckercloudIntegrationStatusEnum.VERIFIED]: 'Verified',
  [TruckercloudIntegrationStatusEnum.NOT_APPLICABLE]: 'Not applicable',
}

export enum TruckercloudRouteProgressStatus {
  QUEUED = 'QUEUED',
  IN_PROGRESS = 'IN_PROGRESS',
  PAST = 'PAST',
  ERROR = 'ERROR',
}

export enum TruckercloudStopStatus {
  QUEUED = 'QUEUED',
  IN_PROGRESS = 'IN_PROGRESS',
  ERROR = 'ERROR',
  CONFIRMED = 'CONFIRMED',
  NOT_CONFIRMED = 'NOT_CONFIRMED',
}

export enum TruckercloudStopStatusName {
  QUEUED = 'Not Started',
  IN_PROGRESS = 'In progress',
  CONFIRMED = 'Arrived',
  NOT_CONFIRMED = 'Not Confirmed',
  ERROR = 'No Data',
}

export enum JobTrackingDashboardStatus {
  ON_TIME = 'ON_TIME',
  WARNING = 'WARNING',
  NEEDS_ATTENTION = 'NEEDS_ATTENTION',
}

export enum ParsingStatus {
  ACCURATELY_PARSED = 'ACCURATELY_PARSED',
  INACCURATELY_PARSED = 'INACCURATELY_PARSED',
  UNREVIEWED = 'UNREVIEWED',
  NOT_PARSED = 'NOT_PARSED',
}

export const ParsingStatusChoicesMap = {
  [ParsingStatus.ACCURATELY_PARSED]: 'Accurately Parsed',
  [ParsingStatus.INACCURATELY_PARSED]: 'Inaccurately Parsed',
  [ParsingStatus.UNREVIEWED]: 'Unreviewed',
  [ParsingStatus.NOT_PARSED]: 'Not parsed',
}

export enum BrokerCreditRatingCategory {
  UNDETERMINED = 'UNDETERMINED',
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  AT_RISK = 'AT_RISK',
}

export enum BrokerCreditAttribute {
  PAST_DUE_AR_PCT = 'broker_past_due_ar_pct',
}

export enum BrokerCategoryKey {
  CREDIT_RATING = 'credit_rating',
  PAST_DUE_AR_PCT = 'past_due_ar_pct',
}

export enum BrokerCategoryName {
  CREDIT_RATING = 'Credit Rating',
  PAST_DUE_AR_PCT = 'AR Past Due',
}

// TODO: @taylangocmen: refactor these
export const BrokerCreditRatingCategoryTextMap = {
  [BrokerCreditRatingCategory.UNDETERMINED]: 'Undetermined',
  [BrokerCreditRatingCategory.GOOD]: 'Good',
  [BrokerCreditRatingCategory.FAIR]: 'Fair',
  [BrokerCreditRatingCategory.AT_RISK]: 'At risk',
}

export const BrokerCreditRatingBackgroundColorMap = {
  [BrokerCreditRatingCategory.UNDETERMINED]: lightgrey,
  [BrokerCreditRatingCategory.GOOD]: success,
  [BrokerCreditRatingCategory.FAIR]: warning,
  [BrokerCreditRatingCategory.AT_RISK]: error,
}

export const BrokerCreditRatingFontColor = {
  [BrokerCreditRatingCategory.UNDETERMINED]: lightgrey,
  [BrokerCreditRatingCategory.GOOD]: successD1,
  [BrokerCreditRatingCategory.FAIR]: marigoldOrange,
  [BrokerCreditRatingCategory.AT_RISK]: errorD1,
}

export interface LocationVector {
  latitude: number
  longitude: number
  timestamp: string
}

interface Broker {
  id: string
  name: string
  credit_rating: string
  credit_rating_category: {
    credit_rating: BrokerCreditRatingCategory
    past_due_ar_pct: BrokerCreditRatingCategory
  }
  credit_rating_metadata: Record<string, string>
  status: string
  is_carrier_packet_complete: boolean
}

export interface Load {
  id: string
  broker: Broker
  posted_date: string | null
  trip_stops: number
  source: string
  comment?: string
  contact_phone?: string
  contact_phone_booking: string
  contact_email: string
  contact_email_booking: string
  truck_length_ft: number
  equipment_detail?: string
  equipment: EquipmentType[]
  trip_distance_mi: number
  truck_weight_lb: number
  contact_name: string
  total_deadhead_mi: number
  source_id: string
  source_human_id: string
  truck_full_load: boolean
  instant_book: boolean
  standard_book: boolean
  offline_book: boolean
  trip_rate: number
  company_name: string
  estimated_rate: number
  estimated_rate_min: number
  estimated_rate_max: number
  estimated_revenue_per_hour: number
  bid_book: boolean
  stops: LoadStop[]
  date_booked?: string
  origin_deadhead_mi?: number
  integration_status?: ConnectedIntegrationStatusEnum
  origin_pickup_date?: string
  dest_delivery_date?: string
}

export interface BookmarkedLoad {
  date_created: string
  id: string
  load: Load
}

export interface JobsFilters {
  status: JobStatus
  date?: string
  driver?: User
  statusDetail?: JobStatusDetail
}

export interface AssignedUser {
  id: string
  first_name: string
  last_name: string
}

export enum DocumentType {
  BILL_OF_LADING = 'BILL_OF_LADING',
  RATE_CONFIRMATION = 'RATE_CONFIRMATION',
  LUMPER_RECEIPT = 'LUMPER_RECEIPT',
  OTHER = 'OTHER',
}

export interface Document {
  date_created: string
  date_updated: string
  description: string
  human_type: string
  is_editable: boolean
  id: string
  job: string
  type: DocumentType
  uploaded_by: string
  attachments: Attachment[]
}

export interface Job {
  assigned_to: AssignedUser
  assigned_truck: TruckV1 | null
  booking_type: BookingTypes
  broker_policy: string
  confirmed_rate: number
  rate_per_mile: number
  created_by: string
  notes: string
  date_created: string
  date_updated: string
  human_id: string
  id: string
  status: JobStatus
  load: Load
  statements: Statement[]
  status_detail: JobStatusDetail
  documents?: Document[]
  latest_mobile_location: LocationVector
  latest_truck_location: LocationVector
  load_stop_progresses: LoadStopProgress[]
  tracking_current_load_stop_progress: LoadStopProgress
  eld_load_stop_progresses: LoadStopProgress[]
  tracking_current_eld_load_stop_progress: LoadStopProgress
  tracking_status: TrackingStatus
  eld_tracking_status: TrackingStatus
  hos_eld_tracking_status: TrackingStatus
  hos_mobile_tracking_status: TrackingStatus
  team?: SlimTeam
  contacted_load?: Decamelized<ContactedLoad> | null
  abort_reason?: string
  abort_cancel_detail?: string
  recovery_job?: Job | null
  recovery_load?: Load | null
  issues?: Issue[]
  is_request_cancellation_late?: boolean
  cancel_reason?: string
  cancel_request_reason?: string
  cancel_request_detail?: string
  recovery_reason?: string
  payment_in_dispute?: boolean
  hazmat_detected?: boolean
  is_driver_suspended?: boolean
}

export interface ScheduleEvent {
  drive_date_end: string
  off_duty_hours: number
  on_duty_hours: number
  total_hours: number
  scheduleId: string
}

export interface LoadStopProgress {
  date_arrived: string
  date_departed: string
  date_estimated_arrival: string
  date_updated: string
  is_current: boolean
  status: LoadStopProgressStatus
  stop: LoadStop
  tracking_last_location_vector: LocationVector
  last_schedule_event: ScheduleEvent
}

export interface LoadStop {
  id: string
  date_end: string
  date_start: string
  is_appointment_confirmed: boolean
  loading_type: string | null
  location_address1: string
  location_address2: string
  location_city: string
  location_lat: number
  location_long: number
  location_name?: string
  location_state: string
  location_timezone: string
  location_zip: string
  sequence_number: number
  notes: string
  type: StopType
  reference_code: string
  truck_weight_lb: number
  type_detail: string
}

export enum BookingTypes {
  INSTANT = 'INSTANT_BOOK',
  STANDARD = 'STANDARD_BOOK',
  OFFLINE = 'OFFLINE',
  SUBMIT_BID = 'SUBMIT_BID',
  EXTERNAL_BOOK = 'EXTERNAL_BOOK',
  ALL = 'ALL',
}

export enum JobStatus {
  PENDING = 'PENDING',
  CURRENT = 'CURRENT',
  FUTURE = 'FUTURE',
  PAST = 'PAST',
}

export enum JobStatusDetail {
  BIDDING_REQUIRED = 'BIDDING_REQUIRED',
  BOOKED_OFFLINE = 'BOOKED_OFFLINE',
  ACTIVE_NEGOTIATION = 'ACTIVE_NEGOTIATION',
  BROKER_SETUP_REQUIRED = 'BROKER_SETUP_REQUIRED',
  AWAITING_RATE_CONFIRMATION = 'AWAITING_RATE_CONFIRMATION',
  REVIEWING_RATE_CONFIRMATION = 'REVIEWING_RATE_CONFIRMATION',
  ABORTED = 'ABORTED',
  JOB_APPROVED = 'JOB_APPROVED',
  PAYMENT_ISSUED = 'PAYMENT_ISSUED',
  CANCELED = 'CANCELED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
  PENDING_DELIVERY_VERIFICATION = 'PENDING_DELIVERY_VERIFICATION',
  DELIVERY_VERIFICATION_ACCEPTED = 'DELIVERY_VERIFICATION_ACCEPTED', // TODO (ezennag-delete)
  PENDING_PAYMENT_VERIFICATION = 'PENDING_PAYMENT_VERIFICATION',
  PAYMENT_REQUIRED = 'PAYMENT_REQUIRED',
  DELIVERY_VERIFICATION_REJECTED = 'DELIVERY_VERIFICATION_REJECTED',
}

export enum LoadBookingTypes {
  INSTANT = 'INSTANT',
  STANDARD = 'STANDARD',
  ALL = 'ALL',
}

export interface Statement {
  date_created: string
  date_updated: string
  description: string
  id: string
  lease_fee: number
  lease_fee_percent: number
  line_items: LineItem[]
  service_fee: number
  service_fee_percent: number
  subtotal: number
  total: number
  fee_items: FeeItem[]
  type: string
}

export interface LineItem {
  amount: string
  date_created: string
  date_updated: string
  description: string
  type: string
}

export interface FeeItem {
  description: string
  amount: string
  date_created: string
  date_updated: string
}

export enum StopType {
  ORIGIN = 'ORIGIN',
  STOP = 'STOP',
  DESTINATION = 'DESTINATION',
}

export interface RateConParsedFields {
  source_human_id: string
  broker: string
  trip_rate: number
  truck_weight_lb: number
  truck_length_ft: number
  equipment: string[]
  contact_name: string
  contact_phone: string
  stops: any
}

export enum RateConParsedStatus {
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  PROCESSED = 'PROCESSED',
}

export interface Discrepancy {
  old_value: any
  new_value: any
}

export interface ReviewForApprovalResponse {
  status: RateConParsedStatus
  parsed_data?: RateConParsedFields
  rate_confirmation?: Document
  discrepancies?: Record<string, Discrepancy>
  rate_con_parsed_data_id?: string
  schedule_id?: string
  auto_approval_failure_flags?: AutoApprovalFailureFlag[]
}

export interface Issue {
  accessorial_request_type: string
  date_created: string
  description: string
  document: Document
  human_type: string
  human_status: string
  human_status_color: string
  human_status_background_color: string
  id: string
  issue_class: string
  is_accessorial: boolean
  status: string
  status_updated_at_time_difference: string
  status_updated_at: string
  stop_label: string
  stop: LoadStop
  checked_in_at: string
  checked_out_at: string
  reported_by: any
  wait_time: string
  type: string
}

export enum AutoApprovalFailureFlagType {
  // The flag is a warning
  Warning = 'WARNING',
  // The flag is an error
  Error = 'ERROR',
}

export interface AutoApprovalFailureFlag {
  title: string
  description: string
  type: AutoApprovalFailureFlagType
  check: string
}
